<template>
  <v-container :key="reloadKey">
    <HandleImportError
      :dialog="dialog"
      :errorMode="errorMode"
      :errors="errors"
      :showDependencyBtn="showDependencyBtn"
      @createDependencies="createDependencies"
      @openCloseDialog="openCloseDialog"
      @getAdditionalImportedData="getAdditionalImportedData"
    />
    <v-row class="mt-4">
      <v-col cols="8">
        <h3>Import Files</h3>
      </v-col>
      <v-col v-if="isSuperAdmin" cols="4">
        <!-- Segmented so the clerk can see both states. isBackupNeeded reads backwards -
             true means the run skips the dump - but the label has always told the truth. -->
        <BinaryToggle
          id="tmc-transform-import-isBackupNeeded"
          aria-label="Database backup before import: on or off"
          off-label="Backup ON"
          on-label="Backup OFF"
          v-model="isBackupNeeded" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="8">
        <v-select
          id="tmc-transform-import-program"
          v-model="programSelect"
          :items="programList"
          label="Select Program"
          item-text="label"
          item-value="programKey"
          @change="filterPipelineByProgram"
        ></v-select>
      </v-col>
    </v-row>
    <v-row v-if="(selectedProgramDetails || {}).subProgram">
      <v-col cols="8">
        <v-select
          id="tmc-transform-import-subprogram"
          v-model="subProgramSelect"
          :items="subProgramList"
          label="Select Sub Program"
          @change="onSubProgramChange"
        ></v-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="8">
        <v-select
          id="tmc-transform-import-select"
          v-model="pipeline"
          :items="filteredProfiles"
          label="Type of import"
        ></v-select>
      </v-col>
    </v-row>
    <v-row v-if="pipelineHasInputs">
      <ImportFilesBetaInputCols
        :key="pipelineKey"
        :inputs="pipeline.inputs"
        :pipeline="pipeline"
        @inputs="pipeline.inputs = $event"
        @flagParams="pipeLineFlag"
        />
    </v-row>
    <v-row>
      <v-col cols="8">
        <v-file-input :disabled="!pipeline"
          id="tmc-import-file-upload-beta"
          v-model="file"
          accept=".txt,.csv,.prn"
          label="Import (.txt,.csv,.prn)"
          show-size
          clearable
          @change="onFileSelected"
        >
        </v-file-input>
      </v-col>
    </v-row>
    <v-row v-if="isRequiredSftp">
      <v-col cols="8">
        <ImportFileList
          :pipeline="pipeline"
          :programSelect="programSelect"
          @importDataFromFile="importDataFromFile"
        />
      </v-col>
    </v-row>
    <v-row v-if="importing">
      <v-col cols="3">
        <p class="mb-0">{{statusText}}</p>
      </v-col>
      <v-col cols="7">
        <v-progress-linear
          id="tmc-import-progress"
          color="deep-purple accent-4"
          :value="progress"
          :indeterminate="isProgressIndeterminate"
          rounded
          height="6"
        ></v-progress-linear>
      </v-col>
    </v-row>
    <v-row v-if="reload">
      <v-col>
        <v-btn
          id="tmc-btn-import-state-reload-beta"
          rounded @click="reloadMe">
          <v-icon class="mr-2">{{icons.mdiReload}}</v-icon> Load another
        </v-btn>
      </v-col>
      <v-col>
        <a v-if="hasReportTemplate"
          class="py-4"
          :href="dynamicReportUrl()"
          target="_blank">Open the report</a>
      </v-col>
      <v-col>
        <span> Imported in: {{this.timing}}</span>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  mdiReload,
} from '@mdi/js';
import {
  mapActions,
  mapMutations,
  mapGetters,
  mapState,
} from 'vuex';
import ImportFileList from '@/components/bulk/sftp/ImportFilesList.vue';
import BinaryToggle from '../../components/common/base/BinaryToggle.vue';
import ImportFilesBetaInputCols, { parsedPipelineItem, reducePipelineInputValues, missingRequiredInputs } from '../../components/bulk/ImportFilesBetaInputCols.vue';
import { clients } from '../../util/clients';
import {
  dateDiffStr,
  isHealthDeptImports,
  isWorkDayImports,
} from '../../util/shared/tmc-global';
import {
  sanitizeFilename,
  resetWindow,
} from '../../util/shared/vue-global';
import {
  generateReportUrl,
  generateChunckID,
  getImportRedirectionUrl,
} from '../../components/config/TransformMapper.util';
import HandleImportError from './HandleImportError.vue';
import {
  filePreValidate,
  importFileProgramMapperWithBackup,
  uploadPercent,
  batchCounter,
  countFileRows,
  sourceHasHeader,
  rowsToBatches,
  rowsCompleted,
  groupThousands,
  importFailureMessage,
  importBatchErrorMessage,
} from './import.utils';
import { transformMapperFieldsImport, programListForImport, pipelineDefaultImport } from '../../util/shared/transform';
import { isTulsa, filterForCountySpecificProfiles } from '../../util/shared/county-specific.config';
import { isAuthorised } from '../../store/auth/token';
import { clerkImportMessage } from '../../util/shared/treasurer-link';
import { authRoles } from '../../router/roleGroups';

const { backendRest } = clients.direct;

const initdata = () => ({
  form: new FormData(),
  dialog: false,
  errorMode: undefined,
  icons: {
    mdiReload,
  },
  file: undefined,
  pipeline: { ...pipelineDefaultImport },
  importing: false,
  reload: false,
  reloadKey: 0,
  statusText: '',
  progress: 0,
  importPhase: undefined,
  // counted off the file before it is sent, so the server phase can report rows done vs pending
  totalRows: 0,
  rowsDone: 0,
  timing: undefined,
  baseCrudKey: 'transform-mapper-list',
  errors: [],
  programSettings: {
    warSettings: {},
  },
  chunkID: '',
  additionalImportedData: {},
  programListItems: [...programListForImport],
  programSelect: undefined,
  subProgramSelect: undefined,
  sftpDialog: false,
  isBackupNeeded: false,
});

export default {
  name: 'ImportFilesBeta',
  components: {
    BinaryToggle,
    ImportFilesBetaInputCols,
    HandleImportError,
    ImportFileList,
  },
  data: () => initdata(),
  computed: {
    ...mapGetters('base/crud', [
      'items',
    ]),
    ...mapState({
      warSettingItem: (state) => state.SystemConfig.warSettingItem,
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem || {},
    }),
    pipelineItems() {
      return this.items(this.baseCrudKey);
    },
    subProgramList() {
      const subPrograms = (this.pipelines || []).map((p) => p.value.subProgram);
      const { countyName } = this.globalSettingItem;
      let filteredItems = [];
      if (isTulsa(countyName)) {
        filteredItems = [...new Set(subPrograms.filter((p) => p !== null))];
      } else {
        filteredItems = [...new Set(subPrograms.filter((p) => p !== null
        && !isHealthDeptImports(p) && !isWorkDayImports(p)))];
      }
      return filteredItems;
    },
    pipelines() {
      return (this.pipelineItems || [])
        .map((i) => parsedPipelineItem(i))
        .map((i) => ({
          text: i.name,
          value: i,
        }))
        .sort((a, b) => {
          if (a.text < b.text) {
            return -1;
          }
          return 1;
        });
    },
    filteredProfiles() {
      let profiles = [...this.pipelines];
      if (this.selectedProgramDetails.subProgram) {
        const profilesArr = profiles.filter(
          (p) => p.value.subProgram === this.subProgramSelect,
        );
        profiles = profilesArr.sort((a, b) => a.text.localeCompare(b.text, 'en', { numeric: true }));
      }
      const { countyName } = this.globalSettingItem;
      const profilesAfterFilter = profiles.filter(
        (p) => filterForCountySpecificProfiles(p, countyName),
      );
      return profilesAfterFilter;
    },
    pipelineHasInputs() {
      return (this.pipeline.inputs || []).length > 0;
    },
    // remounts the inputs when a different profile is picked, so their
    // values never carry over from the previously selected profile
    pipelineKey() {
      return this.pipeline._id || this.pipeline.name;
    },
    pipelineInputsWithValues() {
      const values = reducePipelineInputValues({ inputs: this.pipeline.inputs });
      return values;
    },
    showDependencyBtn() {
      return this.pipeline.createDependency;
    },
    selectedProgramDetails() {
      return this.programList.find((p) => p.programKey === this.programSelect) || {};
    },
    isPayrollRegister() {
      return this.warSettingItem && this.warSettingItem.payrollRegisterProgram;
    },
    programList() {
      if (!this.isPayrollRegister) {
        return this.programListItems.filter((p) => (p.programKey !== 'PAYROLL'));
      }
      return this.programListItems;
    },
    isRequiredSftp() {
      return ((this.pipeline.customConfig || {}).importProfile || {}).isRequiredSftp;
    },
    isSuperAdmin() {
      return isAuthorised(authRoles.SuperAdmin);
    },
    // the upload always knows how much is left; the server phase only does when the row count came
    // off the file, otherwise all it has is a batch tally and the bar has to spin
    isProgressIndeterminate() {
      if (this.importPhase === 'upload') {
        return false;
      }
      if (this.importPhase === 'server') {
        return this.totalRows < 1;
      }
      return true;
    },
  },
  created() {
    this.loadConfig({ requiredScope: 'warconfig' });
    this.loadConfig({ requiredScope: 'global' });
  },
  methods: {
    ...mapActions('SystemConfig', ['loadConfig']),
    ...mapActions('jobs', [
      'backup',
    ]),
    ...mapMutations('jobs', [
      'setPrefix',
      'setGroup',
      'setCollection',
    ]),
    ...mapActions('config', [
      'loadTransformMappers',
    ]),
    ...mapActions('Bulk', [
      'upsertODTransactions',
      'importBankVoucher',
      'loadImportRunStatus',
    ]),
    ...mapActions([
      'flashError',
      'flashSuccess',
      'flashWarn',
    ]),
    getAdditionalImportedData(val) {
      this.additionalImportedData = { ...val };
    },
    openCloseDialog(value) {
      this.dialog = value;
    },
    filterPipelineByProgram() {
      const criteria = {
        type: 'import',
        program: this.programSelect,
      };
      this.loadTransformMappers({
        criteria,
        fields: { ...transformMapperFieldsImport },
        baseCrudKey: this.baseCrudKey,
      });
      resetWindow(this, initdata(), { programSelect: this.programSelect });
    },
    createDependencies(value) {
      if (value) {
        if (Object.keys(this.additionalImportedData).length > 0) {
          this.form.append('additionalImportedData', JSON.stringify(this.additionalImportedData));
        }
        this.insertUploadedFile(this.form);
      }
    },
    async onFileSelected(file) {
      if (file) {
        await this.importFile(file);
      }
    },
    // the bar has to be driven from here rather than from takeDbBackup, which is where 'importing'
    // used to be turned on -- with the backup switch off that method never runs, so the bar and the
    // status text never appeared for the whole import
    startPhase(phase, text) {
      this.importing = true;
      this.importPhase = phase;
      this.progress = 0;
      this.statusText = text;
    },
    endPhase(text = '') {
      this.importing = false;
      this.importPhase = undefined;
      this.progress = 0;
      this.statusText = text;
    },
    // the row total is what makes the server phase a percentage instead of a spinner, but an import
    // must never be blocked by our inability to pre-read the file
    async measureFile(file) {
      this.rowsDone = 0;
      try {
        this.totalRows = await countFileRows(file, {
          hasHeader: sourceHasHeader(this.pipeline.source),
        });
      } catch (err) {
        this.totalRows = 0;
        console.warn('Could not count the rows in the import file', err);
      }
    },
    // one handler pair per POST: the counter closes over its own offset into that response body
    progressHandlers(workingText) {
      const countBatches = batchCounter();
      const totalBatches = rowsToBatches(this.totalRows, this.pipeline.batchSize);
      return {
        onUploadProgress: (progEvt) => {
          const percent = uploadPercent(progEvt);
          this.importPhase = 'upload';
          this.progress = percent;
          this.statusText = `Uploading file... ${percent}%`;
        },
        onDownloadProgress: (progEvt) => {
          const batches = countBatches(progEvt);
          this.importPhase = 'server';
          if (totalBatches < 1) {
            this.progress = 0;
            this.statusText = batches > 0
              ? `${workingText} (${groupThousands(batches)} batches)`
              : workingText;
            return;
          }
          this.rowsDone = rowsCompleted(batches, this.totalRows, this.pipeline.batchSize);
          // held below 100 until the response actually ends -- the last batch still has to answer
          this.progress = Math.min(99, Math.round((this.rowsDone / this.totalRows) * 100));
          this.statusText = `${workingText} ${groupThousands(this.rowsDone)}`
            + ` of ${groupThousands(this.totalRows)} rows`
            + ` (${groupThousands(this.totalRows - this.rowsDone)} pending)`;
        },
      };
    },
    async insertUploadedFile(form) {
      try {
        form.delete('preValidate');
        form.append('preValidate', false);
        const formHeaders = {
          'Content-Type': 'multipart/form-data',
        };
        this.startPhase('upload', 'Uploading file... 0%');
        const results = await backendRest.post('/transmap/import', form, {
          headers: formHeaders,
          ...this.progressHandlers('Importing...'),
        });

        this.endPhase('Loaded!');
        this.file = undefined;

        const startDt = this.timing;
        const endDt = new Date();
        this.timing = dateDiffStr(startDt, endDt);

        this.reload = true;
        if (!results || !results.data || results.data.ok !== 1) {
          // The run marker first where there is one: it is the server's own count of what
          // landed, and it carries the "do not mark anything paid" instruction. The batch
          // message is the fallback for the imports that keep no marker - it at least names
          // the batch and the reason, where 'Unable to upload file' named neither.
          const run = await this.fetchImportRun();
          this.flashError((run && importFailureMessage(run))
            || importBatchErrorMessage(results)
            || 'Unable to upload file.');
          return false;
        }
        // TICKET-2778: when the file was a released Clerk batch, say whether the clerk was told.
        const clerkMessage = clerkImportMessage(results.data.treasurerLink);
        if (clerkMessage && !clerkMessage.ok) {
          this.flashWarn(clerkMessage.text);
        } else {
          this.flashSuccess(clerkMessage ? `Imported! ${clerkMessage.text}` : 'Imported!');
        }

        this.form = new FormData();
        if (!(this.errors == null) && this.errors.length > 0) {
          this.$router.push({
            name: 'Auto Created Dependencies',
            params: {
              args: this.errors,
              redirectToPage: {
                url: this.dynamicReportUrl(),
                params: this.chunkID,
              },
            },
          });
        } else {
          this.openIfReportTemplate();
        }
        return true;
      } catch (err) {
        // without this the bar is left spinning on a failed upload, which reads as 'still working'
        this.endPhase();
        console.error('Error while upserting imported file', err);
        // The request dying (gateway timeout, dropped connection) does not stop the server loading
        // the file, so say how far it got - and never open the results as if the import had finished.
        const run = await this.fetchImportRun();
        this.flashError(importFailureMessage(run));
      }
      return false;
    },
    // the run marker for this import: undefined when it could not be read, null when this kind of
    // import keeps none (see loadImportRunStatus)
    async fetchImportRun() {
      if (!this.chunkID) {
        return undefined;
      }
      return this.loadImportRunStatus({ chunkID: this.chunkID });
    },
    async takeDbBackup() {
      this.startPhase('backup', 'Backing up the database...');
      let successfulBackup = false;
      if (window.confirm('Would you like to take a backup before bulk changes?')) {
        this.setGroup('tmc-local-backend');
        this.setPrefix(`importfilesprebackup_${this.pipeline.program}`);
        const collection = importFileProgramMapperWithBackup[this.pipeline.program];
        // if mapper will not match with any program, it will get 'undefined'
        this.setCollection(collection);
        successfulBackup = await this.backup();
      }
      if (!successfulBackup) {
        this.endPhase();
        this.file = null;
      }
      return successfulBackup;
    },
    async importFile(file) {
      try {
        const missingInputs = missingRequiredInputs({ inputs: this.pipeline.inputs });
        if (missingInputs.length > 0) {
          this.flashError(`Please select ${missingInputs.join(', ')} before importing.`);
          this.file = null;
          return false;
        }
        this.timing = new Date();

        // backup the db
        if (!this.isBackupNeeded) {
          const successfulBackup = await this.takeDbBackup();
          // if backup failed, return
          if (!successfulBackup) return false;
        } // if backup is not needed, continue

        // counted before the upload so both passes can report rows done vs pending
        this.startPhase('read', 'Reading the file...');
        await this.measureFile(file);

        // import the file
        const form = new FormData();
        form.append('fileName', sanitizeFilename(file.name));
        form.append('transMapName', this.pipeline.name);
        const inputsStr = JSON.stringify(reducePipelineInputValues({
          inputs: this.pipeline.inputs,
          programSettings: this.programSettings,
        }));
        form.append('inputs', inputsStr);
        form.append('data', file);
        form.append('preValidate', true);
        this.chunkID = generateChunckID({ profileName: this.pipeline.name });
        form.append('chunkID', this.chunkID);
        form.append('profileID', this.pipeline._id);
        form.append('batchSize', this.pipeline.batchSize);
        // lets the server keep "loaded X of Y rows" for the results screen to show
        form.append('totalRows', this.totalRows);
        // TICKET-2778: which released Clerk batch this file is, so a successful import acks it
        if (file.treasurerLinkBatchId) {
          form.append('treasurerLinkBatchId', file.treasurerLinkBatchId);
        }

        const formHeaders = {
          'Content-Type': 'multipart/form-data',
        };
        // the pre-validate pass streams the whole file through the destination too, so it is just as
        // long a wait as the import itself and used to run with no feedback at all
        this.startPhase('upload', 'Uploading file... 0%');
        [this.dialog, this.errors, this.errorMode] = await filePreValidate({
          form,
          formHeaders,
          ...this.progressHandlers('Checking the file...'),
        });
        if (this.dialog) {
          // the error dialog owns the screen now; leaving the bar up behind it reads as still working
          this.endPhase();
          this.form = form;
          return false;
        }
        this.insertUploadedFile(form);
        return true;
      } catch (err) {
        this.endPhase();
        console.error(err);
        this.flashError('There was a problem while importing your file.');
        return false;
      }
    },
    dynamicReportUrl() {
      const programWiseSettings = {
        warSettings: this.warSettingItem,
      };
      const redirectionUrl = getImportRedirectionUrl(this.pipeline,
        this.programSelect, programWiseSettings);
      const route = generateReportUrl(
        redirectionUrl,
        this.pipeline.inputs,
        {
          chunkID: this.chunkID,
          program: this.pipeline.program,
        },
      );
      return route;
    },
    hasReportTemplate() {
      return (this.pipeline.reportTemplate || '').length > 0;
    },
    openIfReportTemplate() {
      if (this.hasReportTemplate()) {
        const toUrl = this.dynamicReportUrl();
        window.open(toUrl, '_blank');
      }
    },
    reloadMe() {
      Object.assign(this.$data, initdata());
      this.reloadKey += 1;
    },
    pipeLineFlag(value) {
      this.programSettings.warSettings = value;
    },
    onSubProgramChange() {
      this.pipeline = {};
    },
    async importDataFromFile(fileData) {
      if (fileData) {
        await this.importFile(fileData);
      }
    },
  },
};
</script>
